/**
 * ============================================================================
 * Iron Meridian - Save Service
 * ============================================================================
 * 
 * PURPOSE:
 * Client-side API for managing save slots and replay data.
 * Communicates with D1 via Pages Functions.
 * 
 * Saves are standalone (no campaigns) and named as "Country - Turn X".
 * ============================================================================
 */

const API_BASE = '/api'

// ============================================================================
// HELPERS
// ============================================================================

async function apiCall(endpoint, options = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  })

  const data = await response.json()
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || `API error: ${response.status}`)
  }

  return data
}

// ============================================================================
// COUNTRY NAMES (for display)
// ============================================================================

const COUNTRY_NAMES = {
  'USA': 'United States',
  'CHN': 'China',
  'RUS': 'Russia',
  'IND': 'India',
  'BRA': 'Brazil',
  'GBR': 'United Kingdom',
  'DEU': 'Germany',
  'FRA': 'France',
  'JPN': 'Japan',
  'AUS': 'Australia',
  'CAN': 'Canada',
  'MEX': 'Mexico',
  'ARG': 'Argentina',
  'EGY': 'Egypt',
  'ZAF': 'South Africa',
  'NGA': 'Nigeria',
  'TUR': 'Turkey',
  'KOR': 'South Korea',
  'IDN': 'Indonesia',
  'SAU': 'Saudi Arabia'
}

/**
 * Get country display name from code
 */
export function getCountryName(countryCode) {
  return COUNTRY_NAMES[countryCode] || countryCode || 'Unknown'
}

/**
 * Generate display name as "Country - Turn X"
 */
export function generateSaveDisplayName(playerCountry, turnNumber) {
  const countryName = getCountryName(playerCountry)
  return `${countryName} - Turn ${turnNumber}`
}

// ============================================================================
// SAVE API
// ============================================================================

/**
 * Create a new save slot
 * Save is automatically named as "Country - Turn X"
 * @param {Object} params
 * @param {Object} params.gameState - Current game state
 * @param {Object} params.countries - Countries data
 * @param {Object} [params.diplomacy] - Diplomacy state
 * @param {Object} [params.techState] - Tech state
 * @param {Array} [params.actionQueue] - Pending actions
 * @param {Array} [params.combatLog] - Combat history
 * @param {boolean} [params.isAutosave=false] - Is this an autosave
 * @returns {Promise<{saveId: string, displayName: string, turnNumber: number, createdAt: string}>}
 */
export async function createSave(params) {
  const result = await apiCall('/save/create', {
    method: 'POST',
    body: JSON.stringify(params)
  })
  return result.data
}

/**
 * List all saves
 * @param {boolean} [includeSnapshot=false] - Include full snapshot data
 * @returns {Promise<Array>} Array of saves
 */
export async function listSaves(includeSnapshot = false) {
  const params = new URLSearchParams()
  if (includeSnapshot) params.set('includeSnapshot', 'true')
  
  const result = await apiCall(`/save/list?${params.toString()}`)
  return result.data?.saves || []
}

/**
 * Load a save by ID
 * @param {string} saveId
 * @returns {Promise<Object>} Full save data with snapshot
 */
export async function loadSave(saveId) {
  const result = await apiCall(`/save/load?id=${saveId}`)
  return result.data
}

/**
 * Update an existing save
 * @param {Object} params
 * @param {string} params.saveId - Save to update
 * @param {Object} params.gameState - Current game state
 * @param {Object} params.countries - Countries data
 * @param {Array} [params.actions] - Actions taken this turn
 * @param {Object} [params.results] - Turn results
 * @returns {Promise<{saveId: string, turnNumber: number, updatedAt: string}>}
 */
export async function updateSave(params) {
  const result = await apiCall('/save/update', {
    method: 'PUT',
    body: JSON.stringify(params)
  })
  return result.data
}

/**
 * Delete a save slot
 * @param {string} saveId
 * @returns {Promise<{deleted: string, replayTurnsDeleted: number}>}
 */
export async function deleteSave(saveId) {
  const result = await apiCall(`/save/delete?id=${saveId}`, {
    method: 'DELETE'
  })
  return result.data
}

// ============================================================================
// REPLAY API
// ============================================================================

/**
 * Get replay turns for a save
 * @param {string} saveId
 * @param {boolean} [includeSnapshot=true] - Include full snapshots
 * @returns {Promise<{saveId: string, saveName: string, turnCount: number, turns: Array}>}
 */
export async function getReplayTurns(saveId, includeSnapshot = true) {
  const params = new URLSearchParams({ saveId })
  if (!includeSnapshot) params.set('includeSnapshot', 'false')
  
  const result = await apiCall(`/replay/turns?${params.toString()}`)
  return result.data
}

/**
 * Get a specific turn snapshot
 * @param {string} saveId
 * @param {number} turnNumber
 * @returns {Promise<Object>} Turn data with snapshot
 */
export async function getReplayTurn(saveId, turnNumber) {
  const result = await apiCall(`/replay/turns?saveId=${saveId}&turn=${turnNumber}`)
  return result.data.turn
}

// ============================================================================
// EXPORT/IMPORT
// ============================================================================

/**
 * Export a save to a downloadable JSON object
 * @param {string} saveId
 * @returns {Promise<Object>} Full export data
 */
export async function exportSave(saveId) {
  // Load save data
  const save = await loadSave(saveId)
  
  // Load replay turns
  const replayData = await getReplayTurns(saveId, true)
  
  // Build export object
  return {
    version: 2,
    exportedAt: new Date().toISOString(),
    save: {
      id: save.id,
      displayName: save.displayName,
      turnNumber: save.turnNumber,
      playerCountry: save.playerCountry,
      gameStatus: save.gameStatus,
      metadata: save.metadata,
      createdAt: save.createdAt,
      updatedAt: save.updatedAt,
      snapshot: save.snapshot || {
        gameState: save.gameState,
        countries: save.countries,
        diplomacy: save.diplomacy,
        techState: save.techState,
        actionQueue: save.actionQueue,
        combatLog: save.combatLog
      }
    },
    replayTurns: replayData?.turns || []
  }
}

/**
 * Validate import data shape
 * @param {Object} data - Imported JSON
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateImport(data) {
  const errors = []

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Invalid data format'] }
  }

  if (!data.version || data.version < 1) {
    errors.push('Missing or invalid version')
  }

  if (!data.save) {
    errors.push('Missing save data')
  } else {
    if (!data.save.displayName) errors.push('Missing save display name')
    if (!data.save.snapshot) errors.push('Missing save snapshot')
    if (!data.save.snapshot?.gameState) errors.push('Missing game state in snapshot')
    if (!data.save.snapshot?.countries) errors.push('Missing countries in snapshot')
  }

  if (!Array.isArray(data.replayTurns)) {
    errors.push('Missing or invalid replay turns')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Import a save from JSON data
 * @param {Object} data - Import data
 * @param {string} [newDisplayName] - Optional new name for the save
 * @returns {Promise<{saveId: string}>}
 */
export async function importSave(data, newDisplayName) {
  const validation = validateImport(data)
  if (!validation.valid) {
    throw new Error(`Invalid import data: ${validation.errors.join(', ')}`)
  }

  const { save } = data

  // Create new save with imported data
  const createResult = await createSave({
    gameState: save.snapshot.gameState,
    countries: save.snapshot.countries,
    diplomacy: save.snapshot.diplomacy,
    techState: save.snapshot.techState,
    actionQueue: save.snapshot.actionQueue,
    combatLog: save.snapshot.combatLog,
    metadata: {
      ...save.metadata,
      importedFrom: save.id,
      importedAt: new Date().toISOString()
    }
  })

  return {
    saveId: createResult.saveId,
    displayName: createResult.displayName,
    turnNumber: createResult.turnNumber
  }
}

/**
 * Download export data as a JSON file
 * @param {Object} exportData - Data to download
 * @param {string} filename - Filename (without extension)
 */
export function downloadExport(exportData, filename) {
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
    type: 'application/json' 
  })
  const url = URL.createObjectURL(blob)
  
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Read a file and parse as JSON
 * @param {File} file
 * @returns {Promise<Object>}
 */
export function readImportFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result)
        resolve(data)
      } catch (err) {
        reject(new Error('Invalid JSON file'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

export default {
  // Saves
  createSave,
  listSaves,
  loadSave,
  updateSave,
  deleteSave,
  // Replay
  getReplayTurns,
  getReplayTurn,
  // Export/Import
  exportSave,
  validateImport,
  importSave,
  downloadExport,
  readImportFile,
  // Helpers
  getCountryName,
  generateSaveDisplayName
}
